var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var Team_1;
import { schema } from 'lincd-schema/ontologies/schema';
import { Organization } from 'lincd-schema/shapes/Organization';
import { Person } from 'lincd-schema/shapes/Person';
import { Server } from 'lincd-server-utils/utils/Server';
import { xsd } from 'lincd-xsd/ontologies/xsd.js';
import { XSDDate } from 'lincd-xsd/shapes/XSDDate';
import { ShapeSet } from 'lincd/collections/ShapeSet';
import { Literal } from 'lincd/models';
import { disallowProperty, literalProperty, objectProperty, } from 'lincd/shapes/SHACL';
import { irlcg } from '../ontologies/irlcg.js';
import { getPackageShape, linkedShape } from '../package.js';
import { Meeting } from './Meeting.js';
import { Action } from './Action.js';
let Team = Team_1 = class Team extends Organization {
    get meetings() {
        return Meeting.getSetOf(this.getAll(irlcg.Meeting));
    }
    get teamLeaderMessage() {
        return this.getValue(irlcg.teamLeaderMessage);
    }
    set teamLeaderMessage(val) {
        this.overwrite(irlcg.teamLeaderMessage, new Literal(val));
    }
    /**
     * NOTE: overwriting identifier from Thing to imply that it's a required property for a Team
     */
    get identifier() {
        return this.getValue(schema.identifier);
    }
    set identifier(val) {
        this.overwrite(schema.identifier, new Literal(val));
    }
    /**
     * NOTE: overwriting from schema Thing to imply that it's a required property for a Team
     */
    get name() {
        return this.getValue(schema.name);
    }
    set name(val) {
        this.overwrite(schema.name, new Literal(val));
    }
    // I created team leader as property replacing team founder that use as admin
    get teamLeader() {
        return this.getOneAs(irlcg.teamLeader, Person);
    }
    set teamLeader(val) {
        this.overwrite(irlcg.teamLeader, val.namedNode);
    }
    get nextMeetingDate() {
        return XSDDate.toNativeDate(this.getOne(irlcg.nextMeetingDate));
    }
    set nextMeetingDate(val) {
        this.overwrite(irlcg.nextMeetingDate, XSDDate.fromNativeDate(val).node);
    }
    get faxNumber() {
        return undefined;
    }
    get email() {
        return undefined;
    }
    get legalName() {
        return undefined;
    }
    get address() {
        return undefined;
    }
    get telephone() {
        return undefined;
    }
    get founder() {
        return undefined;
    }
    get attendsEvents() {
        return this.getAllAs(irlcg.attendsEvents, getPackageShape('Event'));
    }
    static addUserToTeam(options) {
        return Server.call(this, 'addUserToTeam', options);
    }
    static isTrialTeam(team) {
        if (!team)
            return false;
        //TODO: make this better, probably with a type check
        return team.id.includes('trial');
    }
    static getAllAccounts() {
        return Server.call(this, 'getAllAccounts');
    }
    static getTeamMeeting() {
        return Server.call(this, 'getTeamMeeting');
    }
    static getAllUserInTheTeam() {
        return Server.call(this, 'getAllUserInTheTeam');
    }
    static getAllTeamsIdentifiers() {
        return Server.call(this, 'getAllTeamsIdentifiers');
    }
    static getAllTeams() {
        return Server.call(this, 'getAllTeams');
    }
    /**
     * Get teams of a user, with optional filtering by team type and inclusion of members.
     *
     * @param user
     * @param teamFilter
     * @param withMembers
     * @returns Team or EventTeam
     */
    static getUserTeams({ user, teamFilter = 'all', withMembers = false, }) {
        return Server.call(this, 'getUserTeams', user, teamFilter, withMembers);
    }
    static getTeamOfUser() {
        return Server.call(this, 'getTeamOfUser');
    }
    static getTeamById(teamId) {
        return Server.call(this, 'getTeamById', teamId);
    }
    static getTeamMembersByTeamId(teamIdentifier) {
        return Server.call(this, 'getTeamMembersByTeamId', teamIdentifier);
    }
    static getTeamOf(person) {
        return Server.call(this, 'getTeamOf', person);
    }
    static getCurrentTeamOf(player) {
        return Server.call(this, 'getCurrentTeamOf', player);
    }
    static createTeam(eventId) {
        return Server.call(this, 'createTeam', eventId);
    }
    // TODO: disable for now, because not used on FE
    // static createTeamMeeting(form) {
    //   return Server.call(this, 'createTeamMeeting', form);
    // }
    static getTeamLeader() {
        return Server.call(this, 'getTeamLeader');
    }
    static assignUserAsTeamLeader(teamId, user) {
        return Server.call(this, 'assignUserAsTeamLeader', teamId, user);
    }
    static isUserTeamLeaderOnCurrentTeam() {
        return Server.call(this, 'isUserTeamLeaderOnCurrentTeam');
    }
    static addTeamMate(firstName, lastName, teamID) {
        return Server.call(this, 'addTeamMate', firstName, lastName, teamID);
    }
    /**
     * returns a map of the team participation for each topic
     * the value is the percentage of team members that have participated in the topic (0-100)
     * @param team if not given the current user's team is used
     */
    static getTeamParticipation(team) {
        return Server.call(this, 'getTeamParticipation', team);
    }
    static getTeamStatistics() {
        return Server.call(this, 'getTeamStatistics');
    }
    static moveTeamMembersToAnotherTeam(user, teamId, newTeamId) {
        return Server.call(this, 'moveTeamMembersToAnotherTeam', user, teamId, newTeamId);
    }
    static addUserToAction(userAccount) {
        return Server.call(this, 'addUserToAction', userAccount);
    }
    static removeTeamMate(user, teamId) {
        return Server.call(this, 'removeTeamMate', user, teamId);
    }
    static switchCurrentTeam(teamId) {
        return Server.call(this, 'switchCurrentTeam', teamId);
    }
    /**
     * Check if a team is using event-type actions (actionE2, actionPW1, actionEC2, etc.).
     * Checks the first action from the first event to determine if team is using event actions.
     * Note: callers must select attendsEvents with actions in their query.
     *
     * @param team - Team with attendsEvents and actions selected
     * @returns true when the team's first event uses event-type actions (not regular actions)
     */
    static isEventAction(team) {
        var _a, _b, _c;
        if (!((_a = team === null || team === void 0 ? void 0 : team.attendsEvents) === null || _a === void 0 ? void 0 : _a.length))
            return false;
        const firstEvent = team.attendsEvents[0];
        const firstActionId = (_c = (_b = firstEvent === null || firstEvent === void 0 ? void 0 : firstEvent.actions) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
        if (!firstActionId)
            return false;
        return Action.isEventAction(firstActionId);
    }
    /**
     * Get isEventMode from the team's first event.
     * Uses explicit event.isEventMode when set; falls back to action-based check for backward compat.
     * Excludes trial teams.
     * @param team - Team with attendsEvents (include isEventMode for explicit, or actions for fallback)
     * @returns true = event mode; false = regular mode
     */
    static getIsEventMode(team) {
        var _a;
        if (!team)
            return false;
        if (Team_1.isTrialTeam(team))
            return false;
        const firstEvent = (_a = team === null || team === void 0 ? void 0 : team.attendsEvents) === null || _a === void 0 ? void 0 : _a[0];
        if (!firstEvent)
            return false;
        if (firstEvent.isEventMode !== undefined &&
            firstEvent.isEventMode !== null) {
            return firstEvent.isEventMode;
        }
        return Team_1.isEventAction(team);
    }
};
Team.targetClass = irlcg.Team;
__decorate([
    objectProperty({
        path: irlcg.Meeting,
        shape: Meeting,
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], Team.prototype, "meetings", null);
__decorate([
    literalProperty({
        path: irlcg.teamLeaderMessage,
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Team.prototype, "teamLeaderMessage", null);
__decorate([
    literalProperty({
        path: schema.identifier,
        datatype: xsd.string,
        description: 'The identifier of this team. Every team has an identifier that can be used as a team number or name.',
        maxCount: 1,
        minCount: 1,
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Team.prototype, "identifier", null);
__decorate([
    literalProperty({
        path: schema.name,
        maxCount: 1,
        datatype: xsd.string,
        description: 'The name of this team. OPTIONAL. All teams have identifiers, only event teams have names.',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Team.prototype, "name", null);
__decorate([
    objectProperty({
        path: irlcg.teamLeader,
        shape: Person,
        maxCount: 1,
        description: 'Leader of the team, responsible for team management and coordination.',
    }),
    __metadata("design:type", Person),
    __metadata("design:paramtypes", [Person])
], Team.prototype, "teamLeader", null);
__decorate([
    literalProperty({
        path: irlcg.nextMeetingDate,
        description: 'Date of the next scheduled team meeting.',
    }),
    __metadata("design:type", Date),
    __metadata("design:paramtypes", [Date])
], Team.prototype, "nextMeetingDate", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Team.prototype, "faxNumber", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Team.prototype, "email", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Team.prototype, "legalName", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Team.prototype, "address", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Team.prototype, "telephone", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Team.prototype, "founder", null);
__decorate([
    objectProperty({
        path: irlcg.attendsEvents,
        shape: ['lincd-irlcg', 'Event'],
        description: 'Events that the team attends.',
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], Team.prototype, "attendsEvents", null);
Team = Team_1 = __decorate([
    linkedShape({
        description: 'A team of players in the peace game. Always has an identifier and team leader, may have a name. Represents game groups with team leaders, meetings, messages, and member management. (group, squad, unit)',
    })
], Team);
export { Team };
//# sourceMappingURL=Team.js.map